import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Settings, ArrowLeft, Send, RefreshCw, CheckCircle2, XCircle,
  Info, AlertTriangle, History,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";

/**
 * Phase X1 — Feb 2026: Admin settings page for the Xero Email Upload
 * Workflow.
 *
 * Route: /admin/settings/xero-upload
 *
 *   • Read/write the Xero upload email + enable toggle
 *   • Batch history table (read-only)
 *
 * Guards on the server side are the source of truth; this UI is a
 * convenience surface only.
 */
export default function AdminXeroUploadSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [ccInput, setCcInput]       = useState("");
  const [enabled, setEnabled]       = useState(false);

  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings/xero-upload");
      setSettings(data);
      setEmailInput(data.xero_upload_email || "");
      setCcInput(data.default_cc || "");
      setEnabled(!!data.enabled);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBatches = useCallback(async () => {
    setBatchesLoading(true);
    try {
      const { data } = await api.get("/admin/payout-runs/xero-upload/batches?limit=50");
      setBatches(data.rows || []);
    } catch {
      setBatches([]);
    } finally {
      setBatchesLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadBatches(); }, [load, loadBatches]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings/xero-upload", {
        xero_upload_email: emailInput,
        default_cc:        ccInput,
        enabled:           enabled,
      });
      setSettings(data);
      toast.success("Xero upload settings saved.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PortalShell>
      <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="admin-xero-upload-page">
        <div className="flex items-center gap-3">
          <Link
            to="/admin/partners/payouts"
            className="text-slate-500 hover:text-slate-800 flex items-center gap-1 text-sm"
            data-testid="admin-xero-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Payouts
          </Link>
        </div>

        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
            <Send className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Xero Upload</h1>
            <p className="text-sm text-slate-600">
              Send paid partner payout statement PDFs to your Xero bills-inbox
              email. Email-only — no Xero API. One email per payout run.
            </p>
          </div>
        </div>

        {/* Settings card */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-xero-settings-card">
          <div className="flex items-center gap-2 mb-4">
            <Settings className="w-4 h-4 text-slate-600" />
            <h2 className="text-base font-bold text-slate-900">Settings</h2>
          </div>

          {loading ? (
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Xero upload email <span className="text-rose-600">*</span>
                </label>
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="bills@yourorg.xero.com"
                  data-testid="admin-xero-email-input"
                  className="w-full max-w-md px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  This is the bills-inbox address Xero gave your organisation.
                  Statement PDFs will be emailed here for ingestion.
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Default CC <span className="text-slate-400 font-normal">(optional, unused unless set)</span>
                </label>
                <input
                  type="email"
                  value={ccInput}
                  onChange={(e) => setCcInput(e.target.value)}
                  placeholder="accounts@example.com"
                  data-testid="admin-xero-cc-input"
                  className="w-full max-w-md px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                />
              </div>

              <label className="flex items-start gap-2 text-sm text-slate-800">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  data-testid="admin-xero-enabled-toggle"
                  className="mt-0.5"
                />
                <div>
                  <div className="font-semibold">Enable Xero upload</div>
                  <div className="text-[11px] text-slate-500">
                    When enabled, admins can send paid payout runs to the
                    configured Xero inbox. Disable to hide the send buttons
                    across the payouts UI.
                  </div>
                </div>
              </label>

              {settings?.last_updated_at && (
                <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-3">
                  Last updated {new Date(settings.last_updated_at).toLocaleString("en-AU")}
                  {settings.last_updated_by ? <> by <span className="font-mono">{settings.last_updated_by}</span></> : null}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button
                  onClick={save} disabled={saving}
                  data-testid="admin-xero-save-btn"
                  className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1.5" />
                  {saving ? "Saving…" : "Save settings"}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Preview safety notice */}
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 flex items-start gap-3" data-testid="admin-xero-preview-notice">
          <Info className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
          <div className="text-xs text-indigo-900">
            <div className="font-semibold mb-1">Preview safety</div>
            In preview, every Xero upload is rerouted to{" "}
            <code className="bg-white/70 rounded px-1">XERO_UPLOAD_PREVIEW_TO</code>{" "}
            (fallback <code className="bg-white/70 rounded px-1">TEST_EMAIL_RECIPIENT</code>).
            The intended Xero inbox is preserved on{" "}
            <code className="bg-white/70 rounded px-1">email_logs.original_to</code>
            {" "}for audit.
          </div>
        </div>

        {/* Batch history */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-xero-batch-history">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-slate-600" />
              <h2 className="text-base font-bold text-slate-900">Batch history</h2>
            </div>
            <Button
              variant="outline" size="sm"
              onClick={loadBatches}
              className="rounded-full"
              data-testid="admin-xero-refresh-batches-btn"
              disabled={batchesLoading}
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${batchesLoading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>

          {batchesLoading && batches.length === 0 ? (
            <div className="text-sm text-slate-500">Loading batch history…</div>
          ) : batches.length === 0 ? (
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <Info className="w-4 h-4" /> No batch uploads yet.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2 font-bold">Batch reference</th>
                    <th className="text-left px-3 py-2 font-bold">Initiated</th>
                    <th className="text-left px-3 py-2 font-bold">By</th>
                    <th className="text-right px-3 py-2 font-bold">Runs</th>
                    <th className="text-right px-3 py-2 font-bold">Success</th>
                    <th className="text-right px-3 py-2 font-bold">Failed</th>
                    <th className="text-left px-3 py-2 font-bold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b) => {
                    const st = b.status;
                    const pill = st === "complete"
                      ? "bg-emerald-100 text-emerald-800"
                      : st === "complete_with_errors"
                        ? "bg-rose-100 text-rose-800"
                        : "bg-amber-100 text-amber-800";
                    return (
                      <tr key={b.batch_reference} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-mono text-xs">{b.batch_reference}</td>
                        <td className="px-3 py-2 text-xs text-slate-600 font-mono">
                          {new Date(b.initiated_at).toLocaleString("en-AU")}
                        </td>
                        <td className="px-3 py-2 text-xs">{b.initiated_by}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{b.total_count}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-emerald-700">{b.success_count}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-rose-700">{b.failed_count}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${pill}`}>
                            {st}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </PortalShell>
  );
}
